import { useMemo } from "react";
import type { Molecule2D } from "@/data/moleculeLibrary";

const ELEMENT_COLOR: Record<string, string> = {
  C: "hsl(var(--foreground))",
  H: "hsl(var(--muted-foreground))",
  O: "hsl(0 70% 55%)",
  N: "hsl(220 80% 60%)",
};

/**
 * Lightweight 2D molecule renderer.
 *
 * - Skeletal style: carbon atoms drawn as line vertices (no label) unless
 *   `showLabel` is set. Heteroatoms (O, N) and explicit hydrogens render
 *   their element label inside a small disc that masks the bond line.
 * - Double bonds render as two parallel lines; triple bonds as three.
 * - Aromatic rings get an inner circle for the delocalized system.
 */
export function SkeletalViewer({
  molecule,
  height = 240,
  ariaLabel,
}: {
  molecule: Molecule2D;
  height?: number;
  ariaLabel: string;
}) {
  const { viewBox, atomScreen } = useMemo(() => {
    const xs = molecule.atoms.map((a) => a.x);
    const ys = molecule.atoms.map((a) => a.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const pad = 25;
    const w = maxX - minX + pad * 2;
    const h = maxY - minY + pad * 2;
    const ox = -minX + pad;
    const oy = -minY + pad;
    return {
      viewBox: `0 0 ${w} ${h}`,
      atomScreen: molecule.atoms.map((a) => ({ x: a.x + ox, y: a.y + oy })),
    };
  }, [molecule]);

  return (
    <div
      style={{ height }}
      className="w-full rounded-md overflow-hidden bg-muted/20 border border-border flex items-center justify-center"
      role="img"
      aria-label={ariaLabel}
    >
      <svg
        viewBox={viewBox}
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Bonds */}
        {molecule.bonds.map((bond, i) => {
          const a = atomScreen[bond.a];
          const b = atomScreen[bond.b];
          const order = bond.order ?? 1;
          // Perpendicular offset for multi-bonds
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const len = Math.hypot(dx, dy) || 1;
          const px = (-dy / len) * 4;
          const py = (dx / len) * 4;
          const stroke = "hsl(var(--foreground))";
          const strokeWidth = 1.6;
          if (order === 2) {
            return (
              <g key={i}>
                <line x1={a.x + px} y1={a.y + py} x2={b.x + px} y2={b.y + py} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />
                <line x1={a.x - px} y1={a.y - py} x2={b.x - px} y2={b.y - py} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />
              </g>
            );
          }
          if (order === 3) {
            return (
              <g key={i}>
                <line x1={a.x + px * 1.5} y1={a.y + py * 1.5} x2={b.x + px * 1.5} y2={b.y + py * 1.5} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />
                <line x1={a.x - px * 1.5} y1={a.y - py * 1.5} x2={b.x - px * 1.5} y2={b.y - py * 1.5} stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />
              </g>
            );
          }
          return (
            <line
              key={i}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
          );
        })}

        {/* Aromatic ring inner circle */}
        {molecule.aromaticRing && (() => {
          // Translate ring center using same offset
          const a0 = molecule.atoms[0];
          const s0 = atomScreen[0];
          const ox = s0.x - a0.x;
          const oy = s0.y - a0.y;
          const ring = molecule.aromaticRing;
          return (
            <circle
              cx={ring.cx + ox}
              cy={ring.cy + oy}
              r={ring.r}
              fill="none"
              stroke="hsl(var(--foreground))"
              strokeWidth={1.4}
              opacity={0.55}
            />
          );
        })()}

        {/* Atoms */}
        {molecule.atoms.map((atom, i) => {
          const s = atomScreen[i];
          const showLabel = atom.showLabel || atom.el !== "C";
          if (!showLabel) return null;
          const color = ELEMENT_COLOR[atom.el] ?? "hsl(var(--foreground))";
          return (
            <g key={i}>
              {/* Background disc to mask underlying bond */}
              <circle cx={s.x} cy={s.y} r={9} fill="hsl(var(--background))" />
              <text
                x={s.x}
                y={s.y}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize="13"
                fontFamily="ui-sans-serif, system-ui, sans-serif"
                fontWeight={600}
                fill={color}
              >
                {atom.el}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}